/**
 * Structured tokens in the inventory search box — `game:pokemon set:"Surging Sparks" cond:nm,lp
 * price>20 stock:soldout` — split from the free words that are left to search with.
 *
 * One file, two copies: backend/src/utils/inventory-query-tokens.ts and
 * frontend/src/pages/Inventories/Inventory/inventoryQueryTokens.ts are identical, and the frontend
 * test fails if they differ and runs backend/src/utils/inventory-query-tokens.fixtures.json through
 * both. Edit one, copy it over. The page turns recognised tokens into filter chips; the list endpoint
 * parses whatever is still in `search` with the same rules, so a typed (or shared, or API) query
 * means the same thing before and after it becomes chips. Keep this module dependency-free.
 *
 * Rules:
 * - `key:value`, `key:"quoted value"`, and `key:a,b` (several values, OR-ed). A quoted value is
 *   one value, commas and all; an unclosed quote runs to the end of the input.
 * - `price>N`, `price<N` (also `>=`/`<=`, an optional `$`, up to two decimals) — bounds in the
 *   currency the page shows prices in; both ends are inclusive, like the price range filter.
 * - A token whose key is unknown, or whose value is not one the key accepts, stays free text —
 *   it is never silently dropped.
 * - `game:` / `set:` values are names to resolve (see resolveNamedValues); `printing:` values are
 *   title-cased to match how printings are stored; `cond:` accepts the condition names and their
 *   short forms (nm, lp, mp, hp, dmg).
 * - `tag:` values are storefront tags, kept as typed (`tag:"Booster Box"`, `tag:staff-pick,sale`)
 *   with inner spaces collapsed; a value with a comma is no tag (Shopify splits tags on commas).
 *   They match case-insensitively, a whole tag at a time — see the list endpoint for exactly what.
 */

export type InventoryTokenKey =
  | 'game'
  | 'set'
  | 'condition'
  | 'printing'
  | 'kind'
  | 'channel'
  | 'stock'
  | 'sync'
  | 'status'
  | 'tag'
  | 'minPrice'
  | 'maxPrice'

export type InventoryQueryToken = {
  key: InventoryTokenKey
  /** Canonical values (several = OR). For minPrice/maxPrice, one decimal amount such as "20.5". */
  values: string[]
  /** The token exactly as typed, for chips and for putting it back as text. */
  raw: string
}

export type ParsedInventoryQuery = {
  /** The free words, tokens removed, whitespace collapsed. */
  text: string
  tokens: InventoryQueryToken[]
}

/**
 * What a product is, for `kind:` and the Kind filter — decided from columns the database can
 * index, never from the name:
 * - card: a catalogue single — linked to a card the catalogue does not mark sealed (cards.kind), and
 *   filed as a single (category `single_card`, or no category and type `card`);
 * - sealed: a TCG category other than singles (packs, boxes, ETBs, bundles, decks, promos,
 *   other sealed), or no category and type box/bundle/deck/promo, or no category and a catalogue
 *   card marked sealed (booster boxes / ETBs ingested from the price feed);
 * - accessory: a Models / Storage / Play / Merch category, or no category and type `accessory`;
 * - custom: no catalogue card at all (cardId is null) — overlaps sealed/accessory on purpose.
 * Names are never consulted (no index serves them), so a custom row without a category that is
 * really sealed but typed `card` (the quick-add default) is neither card nor sealed until it is
 * given a category — it still shows under custom.
 */
export const INVENTORY_KINDS = ['card', 'sealed', 'accessory', 'custom'] as const
export type InventoryKind = (typeof INVENTORY_KINDS)[number]

/** `custom`/`default` = per-product channel override or not; the rest = listed on that platform. */
export const INVENTORY_CHANNELS = ['shopify', 'ebay', 'woocommerce', 'custom', 'default'] as const
export type InventoryChannel = (typeof INVENTORY_CHANNELS)[number]

export const INVENTORY_STOCK_VALUES = ['in', 'soldout', 'all'] as const
export type InventoryStockValue = (typeof INVENTORY_STOCK_VALUES)[number]

/** pending = queued for a push (needsSync); error = a channel recorded a push error; ok = neither; held = held from pushing. */
export const INVENTORY_SYNC_STATES = ['pending', 'error', 'ok', 'held'] as const
export type InventorySyncState = (typeof INVENTORY_SYNC_STATES)[number]

/**
 * draft = forced to draft (listingStatus); preorder = pre-order mode on a set that has not
 * released yet; active = not forced to draft (pre-orders included).
 */
export const INVENTORY_LISTING_STATUSES = ['active', 'draft', 'preorder'] as const
export type InventoryListingStatus = (typeof INVENTORY_LISTING_STATUSES)[number]

const KEY_ALIASES: Record<string, InventoryTokenKey> = {
  game: 'game',
  set: 'set',
  cond: 'condition',
  condition: 'condition',
  printing: 'printing',
  kind: 'kind',
  channel: 'channel',
  stock: 'stock',
  sync: 'sync',
  status: 'status',
  tag: 'tag',
  tags: 'tag',
}

const CONDITION_ALIASES: Record<string, string> = {
  nm: 'Near Mint',
  'near mint': 'Near Mint',
  lp: 'Lightly Played',
  'lightly played': 'Lightly Played',
  mp: 'Moderately Played',
  'moderately played': 'Moderately Played',
  hp: 'Heavily Played',
  'heavily played': 'Heavily Played',
  dmg: 'Damaged',
  dm: 'Damaged',
  damaged: 'Damaged',
  sealed: 'Sealed',
}

const ENUM_ALIASES: Partial<Record<InventoryTokenKey, Record<string, string>>> = {
  kind: {
    card: 'card', cards: 'card', single: 'card', singles: 'card',
    sealed: 'sealed',
    accessory: 'accessory', accessories: 'accessory',
    custom: 'custom',
  },
  channel: {
    shopify: 'shopify', ebay: 'ebay', woocommerce: 'woocommerce', woo: 'woocommerce',
    custom: 'custom', default: 'default',
  },
  stock: {
    in: 'in', instock: 'in', 'in-stock': 'in',
    soldout: 'soldout', 'sold-out': 'soldout', out: 'soldout', '0': 'soldout',
    all: 'all',
  },
  sync: { pending: 'pending', error: 'error', errors: 'error', ok: 'ok', synced: 'ok', held: 'held' },
  status: { active: 'active', draft: 'draft', preorder: 'preorder', 'pre-order': 'preorder' },
}

const PRICE_TOKEN = /^price(>=|<=|>|<)\$?(\d+(?:\.\d{1,2})?)$/i

const titleCase = (value: string) =>
  value.toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (_match, lead: string, letter: string) => lead + letter.toUpperCase())

/** Canonical values for `key`, or null when any value is not one the key accepts. */
function canonicalValues(key: InventoryTokenKey, values: string[]): string[] | null {
  if (values.length === 0) return null
  const out: string[] = []
  for (const value of values) {
    const lower = value.toLowerCase()
    let canonical: string | undefined
    if (key === 'condition') canonical = CONDITION_ALIASES[lower]
    else if (key === 'printing') canonical = titleCase(value)
    else if (key === 'game' || key === 'set') canonical = value
    else if (key === 'tag') canonical = value.includes(',') ? undefined : value.replace(/\s+/g, ' ')
    else canonical = ENUM_ALIASES[key]?.[lower]
    if (!canonical) return null
    if (!out.includes(canonical)) out.push(canonical)
  }
  // Stock is one choice, not a set: "in,soldout" is just "all" spelled differently.
  if (key === 'stock' && out.length > 1) return ['all']
  return out
}

/** Split a raw `key:...` value into its values (a quoted value is one value). */
function splitValues(rawValue: string, quoted: boolean): string[] {
  if (quoted) {
    const value = rawValue.trim()
    return value ? [value] : []
  }
  return rawValue.split(',').map((part) => part.trim()).filter(Boolean)
}

/** One whitespace-separated piece, with a `key:"..."` piece kept whole across spaces. */
type Piece = { raw: string; key?: string; value?: string; quoted?: boolean }

function scan(input: string): Piece[] {
  const pieces: Piece[] = []
  let i = 0
  while (i < input.length) {
    if (/\s/.test(input[i])) { i++; continue }
    const keyMatch = /^([A-Za-z]+):"/.exec(input.slice(i))
    if (keyMatch) {
      const valueStart = i + keyMatch[0].length
      const close = input.indexOf('"', valueStart)
      const end = close === -1 ? input.length : close + 1
      pieces.push({
        raw: input.slice(i, end),
        key: keyMatch[1],
        value: input.slice(valueStart, close === -1 ? input.length : close),
        quoted: true,
      })
      i = end
      continue
    }
    let end = i
    while (end < input.length && !/\s/.test(input[end])) end++
    const raw = input.slice(i, end)
    const plain = /^([A-Za-z]+):(.+)$/.exec(raw)
    pieces.push(plain ? { raw, key: plain[1], value: plain[2], quoted: false } : { raw })
    i = end
  }
  return pieces
}

export function parseInventoryQuery(input: string | null | undefined): ParsedInventoryQuery {
  const tokens: InventoryQueryToken[] = []
  const text: string[] = []
  for (const piece of scan(String(input ?? ''))) {
    const price = PRICE_TOKEN.exec(piece.raw)
    if (price) {
      const amount = String(Number(price[2]))
      tokens.push({ key: price[1].startsWith('>') ? 'minPrice' : 'maxPrice', values: [amount], raw: piece.raw })
      continue
    }
    const key = piece.key ? KEY_ALIASES[piece.key.toLowerCase()] : undefined
    const values = key ? canonicalValues(key, splitValues(piece.value ?? '', Boolean(piece.quoted))) : null
    if (key && values) {
      tokens.push({ key, values, raw: piece.raw })
    } else {
      text.push(piece.raw)
    }
  }
  return { text: text.join(' '), tokens }
}

/** Every token's values merged per key (union; for stock and the price bounds the last one wins). */
export function mergeInventoryTokens(tokens: InventoryQueryToken[]): Partial<Record<InventoryTokenKey, string[]>> {
  const merged: Partial<Record<InventoryTokenKey, string[]>> = {}
  for (const token of tokens) {
    if (token.key === 'stock' || token.key === 'minPrice' || token.key === 'maxPrice') {
      merged[token.key] = [...token.values]
      continue
    }
    const existing = merged[token.key] ?? []
    for (const value of token.values) if (!existing.includes(value)) existing.push(value)
    merged[token.key] = existing
  }
  return merged
}

/**
 * Which catalogue entries a typed `game:`/`set:` value names. Exact (case-insensitive) matches on
 * an entry's `exact` keys win — `set:sv08` is that set code, not every set whose name contains it;
 * otherwise every entry whose `contains` keys contain the value. Several values = the union.
 */
export function resolveNamedValues<T>(
  values: string[],
  items: readonly T[],
  keys: (item: T) => { exact: Array<string | null | undefined>; contains: Array<string | null | undefined> },
): T[] {
  const found = new Set<T>()
  for (const value of values) {
    const needle = value.trim().toLowerCase()
    if (!needle) continue
    const exact = items.filter((item) => keys(item).exact.some((key) => key?.toLowerCase() === needle))
    const matches = exact.length > 0
      ? exact
      : items.filter((item) => keys(item).contains.some((key) => key?.toLowerCase().includes(needle)))
    for (const item of matches) found.add(item)
  }
  return [...found]
}

export const gameMatchKeys = (game: { name: string; slug?: string | null }) => ({
  exact: [game.name, game.slug],
  contains: [game.name],
})

export const setMatchKeys = (set: { setName: string; setCode: string }) => ({
  exact: [set.setCode, set.setName],
  contains: [set.setName],
})
